<template>
  <div class="card">
    <div class="bg-blue-light p-2">
      <TitleButton :showBtn="false" title="Payment Requisition" />

      <DateQuerySetter
        :inEnoughSpace="false"
        btnTitle="Go"
        @onClickGo="onClickGo"
      >
        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <div class="d-flex align-items-center justify-content-between filter-label">
            <label class="col-form-label">Party: </label>
            <span class="filter-search-icon cursor-pointer" @click="onOpenContactSearchModal">
              <i class="fas fa-search"></i>
            </span>
          </div>
          <AsyncSelect
            placeholder="Select Party"
            v-model="contactProfile"
            :api-service="fetchContactProfiles"
            :format-label="formatPatientLabel"
          />
        </div>
      </DateQuerySetter>
    </div>

    <div class="px-3 mt-2 d-flex justify-content-between align-items-center">
      <ul class="nav nav-tabs custom-nav-tab mb-0">
        <li v-for="tab in tabs" :key="tab.id" class="nav-item">
          <a
            :class="['nav-link', { active: activeTab === tab.id }]"
            @click="setActiveTab(tab.id)"
          >
            {{ tab.title }}
          </a>
        </li>
      </ul>
      <button
        v-if="isOnApprovalTab"
        class="btn btn-primary btn-sm"
        :disabled="! hasSelection"
        @click="onClickTopSheet"
      >
        <i class="fa fa-print"></i> Top Sheet
      </button>
    </div>

    <div class="card mt-1 p-2">
      <div class="table-responsive">
        <table class="table table-bordered">
          <thead>
            <tr>
              <th v-if="isOnApprovalTab" class="text-center" style="width: 40px;">
                <input type="checkbox" :checked="allSelected" @change="toggleSelectAll" title="Select all" />
              </th>
              <th>Voucher Create Date</th>
              <th>Voucher No</th>
              <th>Party Details</th>
              <th>Item / Account Head</th>
              <th class="text-end">Amount</th>
              <th>Attachment</th>
              <th>Cheque No</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="! rows.length">
              <td :colspan="emptyColspan" class="text-center">No data found.</td>
            </tr>
            <tr v-for="item in rows" :key="activeTab + '-' + item.ledger_master_id">
              <td v-if="isOnApprovalTab" class="text-center">
                <input type="checkbox" :checked="isSelected(item)" @change="toggleSelect(item)" />
              </td>
              <td>{{ item.date }}</td>
              <td>{{ item.voucher_no }}</td>
              <td>{{ item.party_name }}</td>
              <td>{{ item.account_head_display }}</td>
              <td class="text-end">{{ commaFormat(item.amount) }}</td>
              <td>
                <button
                  v-if="item.attachments && item.attachments.length"
                  class="btn btn-outline-secondary btn-sm"
                  title="View attachment"
                  @click="viewAttachments(item.attachments)"
                >
                  <i class="fas fa-paperclip"></i>
                </button>
              </td>
              <td>
                <div class="d-flex align-items-center gap-2">
                  <span v-if="item.cheque_number">{{ item.cheque_number }}</span>
                  <button
                    v-if="activeTab === 'pending'"
                    class="btn btn-outline-primary btn-sm text-nowrap"
                    @click="onClickChequeUpdate(item)"
                    :title="item.cheque_number ? 'Edit Cheque' : 'Add Cheque'"
                  >
                    <i v-if="item.cheque_number" class="fa fa-pencil-alt"></i>
                    <span v-else>Add Cheque</span>
                  </button>
                  <button
                    v-if="activeTab === 'on_approval' && item.cheque_number"
                    class="btn btn-success btn-sm"
                    @click="onClickPrintCheque(item)"
                    title="Print Cheque"
                  >
                    <i class="fa fa-print"></i>
                  </button>
                  <i
                    v-if="activeTab === 'on_approval' && item.cheque_status === 'printed'"
                    class="fa fa-check-circle text-success"
                    title="Cheque Printed"
                  ></i>
                </div>
              </td>
              <td>
                <span>{{ item.status }}</span>
              </td>
              <td>
                <button
                  v-if="activeTab === 'pending'"
                  class="btn btn-primary btn-sm"
                  @click="onClickRequest(item)"
                >Request</button>
                <button
                  v-else
                  class="btn btn-info btn-sm me-1"
                  @click="onClickDetails(item)"
                  title="Details"
                >
                  <i class="fa fa-eye"></i>
                </button>
                <button
                  v-if="activeTab === 'on_approval'"
                  class="btn btn-secondary btn-sm"
                  @click="onClickVoucherPdf(item)"
                  title="Payment Request"
                >
                  <i class="fa fa-file-pdf"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <Pagination
        :offset="offset"
        @onPageChange="onPageChange"
        ref="paginationRef"
      />

      <ContactDropdownFilter
        v-if="store.state.isModalOpenThree"
        @onSearchContact="onSearchContact"
        :isFilterable="true"
        :companyRoles="companyRoles"
      />
    </div>

    <SelectChequeForApprovalModal
      v-if="showChequeModal && activeLedger"
      :ledger-master-id="activeLedger.ledger_master_id"
      :preselected-cheque-id="activeLedger.cheque_register_id"
      :preselected-account-head-id="activeLedger.account_head_id"
      @close="onCloseChequeModal"
      @approved="onApprovalStarted"
    />

    <LedgerDetailsModal
      v-if="showDetailsModal && activeDetailsLedgerId"
      :ledger-master-id="activeDetailsLedgerId"
      @close="onCloseDetailsModal"
    />

    <ChequePrint
      v-if="showChequePrint && chequePrintItem"
      :item="chequePrintItem"
      @onPrinted="fetchData"
      @close="onCloseChequePrint"
    />

    <ViewAttachmentModal
      ref="viewAttachmentRef"
      :attachments="modalAttachments"
    />

    <Loader v-if="loading" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, inject, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import TitleButton from '@/components/atom/TitleButton'
import Loader from '@/components/atom/LoaderComponent'
import DateQuerySetter from '@/components/atom/DateQuerySetter'
import Pagination from '@/components/atom/Pagination'
import AsyncSelect from '@/components/molecule/input-field/AsyncSelect.vue'
import ContactDropdownFilter from '@/components/atom/Contact/ContactDropdownFilter.vue'
import SelectChequeForApprovalModal from './SelectChequeForApprovalModal.vue'
import LedgerDetailsModal from './LedgerDetailsModal.vue'
import ChequePrint from '@/views/page/company/core-accounting/cheque-register/ChequePrint'
import ViewAttachmentModal from '@/views/page/company/ngo/voucher/payment/ViewAttachmentModal.vue'
import handlePaymentRequisition from '@/services/modules/paymentRequisition'
import handleContact from '@/services/modules/contact'
import handleRole from '@/services/modules/role'
import handleCompany from '@/services/modules/company'
import handleBarcode from '@/services/modules/barcode'
import figureFormatter from '@/services/utils/figureFormatter'
import paymentRequestVoucherPdf from '@/services/utils/paymentRequestVoucherPdf'
import PaymentRequisitionTopSheetPdf from '@/services/utils/pdf/PaymentRequisitionTopSheetPdf'
import { useAsyncDropdownHelper } from '@/services/utils/asyncDropdownHelper'

const showError = inject('showError')
const showSuccess = inject('showSuccess')
const store = useStore()
const route = useRoute()
const router = useRouter()

const { fetchPaymentRequisitionList, startLedgerApproval, fetchPaymentRequisitionVoucherPdf, fetchPaymentRequisitionTopSheet } = handlePaymentRequisition()
const { fetchContactProfiles } = handleContact()
const { fetchCompanyInfo } = handleCompany()
const { fetchBarcode } = handleBarcode()
const { formatPatientLabel } = useAsyncDropdownHelper()
const { fetchCompanyDefaultRoles } = handleRole()
const { commaFormat } = figureFormatter()
const { exportToPDF } = paymentRequestVoucherPdf()
const { exportToPDF: exportTopSheetToPDF } = PaymentRequisitionTopSheetPdf()

const tabs = [
  { id: 'pending', title: 'Pending' },
  { id: 'on_approval', title: 'On Approval' },
  { id: 'approved', title: 'Approved' },
]

const tabStatusMap = {
  pending: 'pending_approval',
  on_approval: 'pending_review,pending_approve,on_approval,pa_approved,rejected,returned',
  approved: 'approved',
}

const activeTab = ref('pending')
const loading = ref(false)
const rows = ref([])
const contactProfile = ref(null)
const companyRoles = ref([])
const offset = ref(50)
const paginationRef = ref(null)
const showChequeModal = ref(false)
const activeLedger = ref(null)
const showDetailsModal = ref(false)
const activeDetailsLedgerId = ref(null)
const showChequePrint = ref(false)
const chequePrintItem = ref(null)
const selectedIds = ref([])
const modalAttachments = ref([])
const viewAttachmentRef = ref(null)

const companyId = computed(() => route.params.companyId)
const isOnApprovalTab = computed(() => activeTab.value === 'on_approval')
const hasSelection = computed(() => selectedIds.value.length > 0)
const allSelected = computed(() => rows.value.length > 0 && selectedIds.value.length === rows.value.length)
const emptyColspan = computed(() => {
  if (isOnApprovalTab.value) return 10
  return 9
})
const start = computed(() => route.query.start)
const end = computed(() => route.query.end)
const page = computed(() => route.query.page)

const getQuery = () => {
  let query = '?company_id=' + companyId.value
  if (start.value) query += '&start_date=' + start.value
  if (end.value) query += '&end_date=' + end.value
  if (page.value) query += '&page=' + page.value
  if (contactProfile.value) query += '&contact_profile_id=' + contactProfile.value.id
  query += '&status=' + tabStatusMap[activeTab.value]
  query += '&offset=' + offset.value
  return query
}

const setPagination = (data) => {
  if (! data) return
  store.commit('setPaginationData', {
    records: data.total,
    perPage: data.per_page,
    currentPage: data.current_page,
  })
}

const fetchData = async () => {
  loading.value = true
  rows.value = []
  selectedIds.value = []
  try {
    const res = await fetchPaymentRequisitionList(getQuery())
    if (! res.status) {
      showError(res.message)
      store.commit('resetPagination')
      return
    }
    rows.value = res.data.data
    setPagination(res.data)
  } catch (err) {
    showError('Something is wrong. Check your connectivity!!')
  } finally {
    loading.value = false
  }
}

const resetToFirstPage = async () => {
  store.commit('setPaginationData', {})
  const routeQuery = Object.assign({}, route.query)
  routeQuery.page = 1
  await router.push({ path: route.path, query: routeQuery }).catch(function () {})
}

const setActiveTab = async (id) => {
  if (activeTab.value === id) return
  activeTab.value = id
  await resetToFirstPage()
  await fetchData()
}

const onClickGo = async () => {
  await resetToFirstPage()
  await fetchData()
}

const onPageChange = async (p) => {
  const routeQuery = Object.assign({}, route.query)
  routeQuery.page = p
  await router.push({ path: route.path, query: routeQuery }).catch(function () {})
  await fetchData()
}

const onClickChequeUpdate = (item) => {
  activeLedger.value = {
    ledger_master_id: item.ledger_master_id,
    cheque_register_id: item.cheque_register_id || null,
    account_head_id: item.cheque_account_head_id || null,
  }
  showChequeModal.value = true
}

const onClickRequest = async (item) => {
  if (! window.confirm('Are you sure you want to request?')) return
  loading.value = true
  try {
    const res = await startLedgerApproval(item.ledger_master_id, {
      company_id: companyId.value,
      page_id: Number(route.params.pageId) || null,
    })
    if (! res.status) {
      showError(res.message)
      return
    }
    showSuccess(res.message)
    await generateVoucherPdf(item.ledger_master_id)
    fetchData()
  } catch (err) {
    showError('Something is wrong. Check your connectivity!!')
  } finally {
    loading.value = false
  }
}

const generateVoucherPdf = async (ledgerMasterId) => {
  const companyQuery = `?company_id=${companyId.value}`
  const [pdfRes, company] = await Promise.all([
    fetchPaymentRequisitionVoucherPdf(ledgerMasterId, companyQuery),
    fetchCompanyInfo(companyId.value),
  ])
  if (! pdfRes.status) {
    showError(pdfRes.message)
    return
  }
  const voucher = pdfRes.data
  const qrcodeData = JSON.stringify({
    ref_no: voucher.voucher_no,
    ref_date: voucher.date,
    created_date_time: new Date().toISOString(),
  })
  const barcodeRes = await fetchBarcode(`?code=${voucher.voucher_no}&qrcode=${qrcodeData}`)
  exportToPDF(company.data, voucher, barcodeRes.qrcode)
}

const onApprovalStarted = () => {
  fetchData()
}

const onCloseChequeModal = () => {
  showChequeModal.value = false
  activeLedger.value = null
}

const onClickDetails = (item) => {
  activeDetailsLedgerId.value = item.ledger_master_id
  showDetailsModal.value = true
}

const onClickVoucherPdf = async (item) => {
  loading.value = true
  try {
    await generateVoucherPdf(item.ledger_master_id)
  } catch (err) {
    showError('Something is wrong. Check your connectivity!!')
  } finally {
    loading.value = false
  }
}

const isSelected = (item) => selectedIds.value.includes(item.ledger_master_id)

const toggleSelect = (item) => {
  if (isSelected(item)) {
    selectedIds.value = selectedIds.value.filter((id) => id !== item.ledger_master_id)
    return
  }
  selectedIds.value = selectedIds.value.concat(item.ledger_master_id)
}

const toggleSelectAll = () => {
  if (allSelected.value) {
    selectedIds.value = []
    return
  }
  selectedIds.value = rows.value.map((item) => item.ledger_master_id)
}

const onClickTopSheet = async () => {
  if (! hasSelection.value) return
  loading.value = true
  try {
    const [res, company] = await Promise.all([
      fetchPaymentRequisitionTopSheet({ company_id: Number(companyId.value), ledger_master_ids: selectedIds.value }),
      fetchCompanyInfo(companyId.value),
    ])
    if (! res.status) {
      showError(res.message)
      return
    }
    exportTopSheetToPDF(company.data, res.data)
  } catch (err) {
    showError('Something is wrong. Check your connectivity!!')
  } finally {
    loading.value = false
  }
}

const onCloseDetailsModal = () => {
  showDetailsModal.value = false
  activeDetailsLedgerId.value = null
}

const onClickPrintCheque = (item) => {
  chequePrintItem.value = {
    id: item.cheque_register_id,
    cheque_date: item.cheque_date,
    contact_profile_name: item.party_name,
    amount: item.amount,
    account_head_id: item.cheque_account_head_id,
    status: item.cheque_status,
  }
  showChequePrint.value = true
}

const onCloseChequePrint = () => {
  showChequePrint.value = false
  chequePrintItem.value = null
}

const viewAttachments = (attachments) => {
  if (! attachments || ! attachments.length) return
  if (! viewAttachmentRef.value) return
  modalAttachments.value = attachments
  viewAttachmentRef.value.toggleModal()
}

const onOpenContactSearchModal = () => {
  store.state.isModalOpenThree = true
}

const onSearchContact = (contact) => {
  contactProfile.value = contact
}

const getCompanyRoles = async () => {
  const res = await fetchCompanyDefaultRoles(`?company_id=${companyId.value}`)
  if (! res.status) return
  companyRoles.value = res.data
}

const clearDateRange = async () => {
  if (! start.value && ! end.value) return
  const routeQuery = Object.assign({}, route.query)
  delete routeQuery.start
  delete routeQuery.end
  await router.push({ path: route.path, query: routeQuery }).catch(function () {})
}

onMounted(async () => {
  store.commit('resetPagination')
  getCompanyRoles()
  await clearDateRange()
  fetchData()
})
</script>

<style scoped>
.filter-label {
  margin-bottom: 4px;
}
</style>
